import { api, toast, inspectorForm, modalConfirm } from '../state.js';
import { esc, badge } from '/core/js/utils.js';

/**
 * <arc-notifications> — where alerts are sent: each email channel, its
 * recipients and the least severity it carries. Every module's alerts go
 * to the same channels. Add a channel opens the form in the drawer; each
 * row has Edit…, an on/off switch and Remove. The platform's since
 * WIREGUARD-PLAN 1.5 (W8); it was the backups module's.
 */
const on = (v) => v === true || v === 't' || v === 1 || v === '1' || v === 'true';
const plural = (n, word, words = `${word}s`) => `${Number(n).toLocaleString('en-US')} ${Number(n) === 1 ? word : words}`;

class ArcNotifications extends HTMLElement {
  #items = [];

  connectedCallback() {
    this.innerHTML = `
      <div class="page-header">
        <div class="detail-title"><h1>Notifications</h1></div>
        <div class="page-header-actions"><button type="button" class="btn btn-primary" data-add>Add a channel</button></div>
      </div>
      <div data-slot="body"><div class="loading-center"><div class="spinner"></div></div></div>`;
    this.querySelector('[data-add]').addEventListener('click', () => this.#form());
    this.querySelector('[data-slot="body"]').addEventListener('click', e => {
      const b = e.target.closest('button[data-channel]');
      if (!b) return;
      const c = this.#items.find(x => String(x.id) === b.dataset.channel);
      if (!c) return;
      if (b.dataset.act === 'edit') this.#form(c);
      if (b.dataset.act === 'remove') this.#remove(c);
    });
    this.querySelector('[data-slot="body"]').addEventListener('change', e => {
      const sw = e.target.closest('input[data-toggle]');
      if (sw) this.#toggle(sw.dataset.toggle, sw.checked);
    });
    this.#load();
  }

  async #load() {
    const body = this.querySelector('[data-slot="body"]');
    try {
      this.#items = (await api.notifications.channels()).items ?? [];
      this.#render();
    } catch (err) {
      body.innerHTML = `<div class="empty-state"><h3>Could not load</h3><p>${esc(err.message)}</p></div>`;
    }
  }

  #form(c = null) {
    inspectorForm({
      title: c ? `Edit ${c.name}` : 'Add a channel',
      submitLabel: c ? 'Save' : 'Add',
      body: `
        <label class="form-field"><span>Name *</span>
          <input name="name" required maxlength="128" value="${esc(c?.name ?? '')}" placeholder="The admins" autocomplete="off"></label>
        <label class="form-field"><span>Email addresses, one per line *</span>
          <textarea name="recipients" rows="4" required>${esc((c?.config?.recipients ?? []).join('\n'))}</textarea></label>
        <label class="form-field"><span>Mail alerts of this severity and up</span>
          <select name="min_severity">
            ${['info', 'warning', 'critical'].map(s => `<option value="${s}" ${(c?.config?.min_severity ?? 'warning') === s ? 'selected' : ''}>${s}</option>`).join('')}
          </select></label>
        <p class="field-hint">Every module's alerts, mailed through ARC's relay (Settings → Mail relay) within 5 minutes of being raised.</p>`,
      onSubmit: async (f) => {
        const body = { name: f.name.value.trim(), type: 'email', config: { recipients: f.recipients.value, min_severity: f.min_severity.value } };
        const res = c ? await api.notifications.updateChannel(c.id, body) : await api.notifications.createChannel(body);
        toast(res.lands?.says ?? 'Saved', 'success');
        this.#load();
      },
    });
  }

  async #toggle(id, checked) {
    try {
      const res = await api.notifications.updateChannel(id, { active: checked });
      toast(res.lands?.says ?? 'Saved', 'success');
      this.#load();
    } catch (err) { toast(err.message, 'error'); this.#load(); }
  }

  async #remove(c) {
    if (!await modalConfirm(`Remove the channel ${c.name}? Nothing more is mailed to ${(c.config?.recipients ?? []).join(', ')}.`,
        { title: 'Remove a channel', confirmText: 'Remove', type: 'danger' })) return;
    try {
      const res = await api.notifications.deleteChannel(c.id);
      toast(res.lands?.says ?? 'Removed', 'success');
      this.#load();
    } catch (err) { toast(err.message, 'error'); }
  }

  #render() {
    const body = this.querySelector('[data-slot="body"]');
    if (!this.#items.length) {
      body.innerHTML = `<div class="empty-state"><h3>No channels</h3>
        <p>Alerts are raised and shown under Alerts, but mailed to nobody until a channel exists.</p></div>`;
      return;
    }
    body.innerHTML = `
      <div class="card">
        <div class="table-wrapper">
          <table>
            <thead><tr><th scope="col" class="nowrap">Channel</th><th scope="col" class="cell-fill">Recipients</th><th scope="col" class="nowrap">Carries</th><th scope="col" class="nowrap">On</th><th scope="col" class="nowrap"></th></tr></thead>
            <tbody>${this.#items.map(c => `
              <tr data-channel="${esc(c.id)}">
                <td class="nowrap"><strong>${esc(c.name)}</strong><div class="text-muted">${esc(c.type)}</div></td>
                <td class="cell-fill">${esc((c.config?.recipients ?? []).join(', '))}</td>
                <td class="nowrap">${badge(c.config?.min_severity === 'critical' ? 'failed' : c.config?.min_severity === 'info' ? 'info' : 'warning', `${c.config?.min_severity ?? 'warning'} and up`)}</td>
                <td class="nowrap"><label class="switch"><input type="checkbox" data-toggle="${esc(c.id)}" ${on(c.active) ? 'checked' : ''}><span class="slider"></span></label></td>
                <td class="text-right nowrap">
                  <button type="button" class="btn btn-ghost btn-sm" data-channel="${esc(c.id)}" data-act="edit">Edit…</button>
                  <button type="button" class="btn btn-ghost btn-sm" data-channel="${esc(c.id)}" data-act="remove">Remove</button></td>
              </tr>`).join('')}</tbody>
          </table>
        </div>
      </div>
      <p class="text-muted">${esc(plural(this.#items.length, 'channel'))}</p>`;
  }
}

customElements.define('arc-notifications', ArcNotifications);
